import React from 'react';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import { DataCollectionGapSummary } from '../../../../store/Models/Api/DataCollectionGapSummaryModels';
import { ToxnotReactModal, ToxnotReactModalBody } from '../../ToxnotReactModal';
import DocumentIcon from '../../../shared/DocumentIcon';
import DocumentPlusIcon from '../../../shared/DocumentPlusIcon';
import { useLocalizer } from '../hooks/useLocalizer';
import {
    getGapReportMaterialsWithGapsCount,
    getGapReportMissingFieldCount
} from './helpers/gapSummaryHelpers';
import BackToGapSummaryButton from './BackToGapSummaryButton';
import StartOverWithNewFilesButton from './StartOverWithNewFilesButton';
import { useGapReportDelivery } from './hooks/useGapReportDelivery';

const composeEmailModalId = 'ai-gap-summary-compose-email-modal';
const compliContactEmailMaxLength = 80;

interface RequestMoreFilesPanelProps {
    requestId: string;
    operationId: string;
    summaryPayload: DataCollectionGapSummary;
    onEmailSent: (recipientEmail: string) => Promise<void>;
    onBack: () => void;
    onStartOver?: () => void;
    setPageErrorMessage: (message: string | null) => void;
}

export const RequestMoreFilesPanel: React.FC<RequestMoreFilesPanelProps> = ({
    requestId,
    operationId,
    summaryPayload,
    onEmailSent,
    onBack,
    onStartOver,
    setPageErrorMessage
}) => {
    const localize = useLocalizer();
    const requestSendToEmail = useSelector((state: ApplicationState) =>
        state.createAndEditSurveysState?.surveyRequest.sendToEmail ?? '');
    const obtainmentServicesEnabled = useSelector((state: ApplicationState) =>
        state.appServices?.userContext?.userCapabilities.obtainmentServicesEnabled === true);

    const materialsWithGaps = React.useMemo(
        () => getGapReportMaterialsWithGapsCount(summaryPayload),
        [summaryPayload]);

    const missingRequiredFields = React.useMemo(
        () => getGapReportMissingFieldCount(summaryPayload),
        [summaryPayload]);

    const {
        recipientEmail,
        setRecipientEmail,
        message,
        setMessage,
        isSending,
        hasSent,
        hasDownloaded,
        composeErrorMessage,
        showComposeModal,
        downloadReport,
        sendReport
    } = useGapReportDelivery({
        requestId,
        operationId,
        defaultRecipientEmail: requestSendToEmail,
        recipientEmailMaxLength: obtainmentServicesEnabled ? compliContactEmailMaxLength : undefined,
        composeModalId: composeEmailModalId,
        onEmailSent
    });

    return (
        <>
            <div className="gap-summary-request-more-files-title">
                <span className="gap-summary-request-more-files-icon">
                    <DocumentPlusIcon className="gap-summary-document-plus-icon" />
                </span>
                <h4>{localize('Fill gaps with supplier input')}</h4>
            </div>

            <section className="gap-summary-callout gap-summary-gap-report-ready">
                <span className="gap-summary-callout-icon gap-summary-gap-report-ready-icon">
                    <DocumentIcon />
                </span>
                <div>
                    <h4>{localize('Gap Report Ready')}</h4>
                    <p>
                        {localize('CSV listing')} {missingRequiredFields} {localize('missing required fields across')} {materialsWithGaps} {localize('materials, grouped by topic')}
                    </p>
                </div>
            </section>

            <div className="gap-summary-report-actions">
                <button type="button" className="btn btn-toxnot-primary gap-summary-report-download" onClick={downloadReport}>
                    <span className="glyphicon glyphicon-download-alt" aria-hidden="true"></span>{' '}
                    {localize('Download Gap Report (.csv)')}
                </button>
                <button
                    type="button"
                    className="btn btn-default gap-summary-report-compose"
                    onClick={showComposeModal}
                    disabled={hasSent}
                >
                    <span className="glyphicon glyphicon-envelope" aria-hidden="true"></span>{' '}
                    {hasSent ? localize('Gap report sent') : localize('Send to Supplier via Email')}
                </button>
            </div>

            <BackToGapSummaryButton
                onBack={onBack}
                trailingAction={(
                    <StartOverWithNewFilesButton
                        onStartOver={onStartOver}
                        setErrorMessage={setPageErrorMessage}
                        title="Process new documents"
                        appearance="action"
                        isPrimary={hasDownloaded}
                    />
                )}
            />

            <ToxnotReactModal
                modalId={composeEmailModalId}
                customHeader={(
                    <h4 className="modal-title gap-summary-compose-email-title">
                        <span className="glyphicon glyphicon-envelope gap-summary-compose-email-title-icon" aria-hidden="true"></span>
                        {localize('Compose Email')}
                    </h4>
                )}
            >
                <ToxnotReactModalBody>
                    {composeErrorMessage && (
                        <div className="alert alert-danger" role="alert">
                            {composeErrorMessage}
                        </div>
                    )}
                    <div className="form-group">
                        <label htmlFor="gap-report-recipient">{localize('To')}</label>
                        <input
                            id="gap-report-recipient"
                            type="email"
                            className="form-control"
                            value={recipientEmail}
                            maxLength={obtainmentServicesEnabled ? compliContactEmailMaxLength : undefined}
                            disabled={isSending}
                            onChange={event => setRecipientEmail(event.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="gap-report-message">{localize('Message')}</label>
                        <textarea
                            id="gap-report-message"
                            className="form-control"
                            rows={9}
                            value={message}
                            disabled={isSending}
                            onChange={event => setMessage(event.target.value)}
                        />
                    </div>
                    <p className="gap-summary-report-attach-note">
                        {localize('Secure links to download the gap report CSV and upload supporting documents will be included automatically.')}
                    </p>
                </ToxnotReactModalBody>
                <div className="modal-footer gap-summary-compose-email-footer">
                    <button type="button" className="btn btn-default gap-summary-compose-email-cancel" data-dismiss="modal" disabled={isSending}>
                        {localize('Cancel')}
                    </button>
                    <button type="button" className="btn btn-toxnot-primary gap-summary-compose-email-send" disabled={isSending} onClick={sendReport}>
                        <span className="glyphicon glyphicon-send gap-summary-compose-email-send-icon" aria-hidden="true"></span>
                        {localize('Send')}
                    </button>
                </div>
            </ToxnotReactModal>
        </>
    );
};

export default RequestMoreFilesPanel;
